import './style.modules.scss'

import { Card, CardRecord } from '@ampcore/adel-react'
import { VideoPlayerProvider } from '@ampcore/video-player'
import React, { useState } from 'react'
import xss from 'xss'

import { VideoCard } from '../VideoCard/VideoCard'
import type { VideoCardRecord } from '../VideoCard/VideoCardRecord.type'
import { DropdownMenu } from './DropdownMenu'

export type CardListProps = {
  cardDescription?: string
  cardList?: CardRecord[] | VideoCardRecord[]
  cardLimit?: string
  buttonLabel?: string
  dropdownBefore?: string
  dropdownAfter?: string
  allLabelValue?: string
  defaultCardCategory?: string
  componentSpacing?: string
  testId?: string | null
}

function isCardVideoRecord(
  card: CardRecord | VideoCardRecord
): card is VideoCardRecord {
  return 'playlistId' in card
}

export const CardList: React.FC<CardListProps> = ({
  cardDescription = '',
  cardList = [],
  cardLimit = '6',
  buttonLabel = 'Load More',
  dropdownBefore = 'View',
  dropdownAfter = 'Articles',
  allLabelValue = 'All',
  defaultCardCategory = '',
  componentSpacing = '',
  testId = null
}) => {
  const categories = React.useMemo((): string[] => {
    const uniqueCategories: string[] = []
    cardList.forEach(card => {
      if (!card.cardCategory || uniqueCategories.includes(card.cardCategory)) {
        return
      }
      uniqueCategories.push(card.cardCategory)
    })
    return [
      allLabelValue,
      ...uniqueCategories.sort((a, b) => a.localeCompare(b))
    ]
  }, [allLabelValue, cardList])

  const getInitialCategory = (): string => {
    // edge case when we override all label value but set "All" as default
    if (defaultCardCategory === 'All' && allLabelValue) {
      return allLabelValue
    }
    return defaultCardCategory || allLabelValue
  }

  const [maxCards, setMaxCards] = useState<number>(Number(cardLimit))
  const [currentCategory, setCurrentCategory] = useState<string>(() =>
    getInitialCategory()
  )

  const handleButtonClick = () => {
    let newMaxCards = maxCards + Number(cardLimit)
    if (newMaxCards > cardList.length) {
      newMaxCards = cardList.length
    }
    setMaxCards(newMaxCards)
  }

  const handleCategoryChange = (category: string) => {
    if (category !== currentCategory) {
      setMaxCards(Number(cardLimit))
    }
    setCurrentCategory(category)
  }

  const cardsToDisplay = cardList.filter(currCard => {
    if (currentCategory === allLabelValue) {
      return true
    }
    return currCard.cardCategory === currentCategory
  })

  return (
    <section
      className={`card-list-component ${componentSpacing}`}
      data-limit={cardLimit}
    >
      <div className="Grid u-sizeConstrained" data-testid={testId ?? undefined}>
        {cardDescription && (
          <div
            className="card-description Content u-marginBottom20"
            dangerouslySetInnerHTML={{ __html: xss(cardDescription) }}
          />
        )}
        {categories.length > 1 && (
          <div className="u-flex u-lg-flexJustifyBetween u-lg-flexRow u-flexCol u-sizeFull u-flexAlignItemsBaseline u-posRelative u-marginBottom20">
            <DropdownMenu
              items={categories}
              onChangeHandler={handleCategoryChange}
              summaryBeforeText={dropdownBefore}
              summaryAfterText={dropdownAfter}
              currentItem={currentCategory}
            />
            <p className="u-sm-paddingTop8 u-md-paddingTop8">{`${
              cardsToDisplay.length
            } Result${cardsToDisplay.length !== 1 ? 's' : ''}`}</p>
          </div>
        )}
        <div className="card-list-wrapper u-sm-flex u-sm-flexCol u-marginBottom20">
          <VideoPlayerProvider>
            {cardsToDisplay
              .slice(0, maxCards)
              .map((card, index) =>
                isCardVideoRecord(card) ? (
                  <VideoCard key={'card' + index} card={card} />
                ) : (
                  <Card key={'card' + index} card={card} />
                )
              )}
          </VideoPlayerProvider>
        </div>
        {maxCards < cardsToDisplay.length && (
          <div className="load-more u-flex u-flexJustifyCenter">
            <button
              className="Button Button--secondary Button--secondary--action-blue"
              onClick={handleButtonClick}
            >
              {buttonLabel}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
